import { Box, Card, CardBody, CardHeader, Divider, Heading, Image, Stack, Text } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { CargoType, DocumentType, FreightService } from '@pelicargo/types';
import { buildFlightPathString, Show } from '@pelicargo/ui';
import { prettyDate, prettyDockHour } from '@pelicargo/utils';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import z from 'zod';

import { CopyButton } from '../../components/CopyButton';
import { CopyLineItemsButton } from '../../components/CopyLineItemsButton';
import { DocumentsForm } from '../../components/Documents/DocumentsForm ';
import { DropOffSection } from '../../components/DropOffSection';
import { InstructionsCard } from '../../components/InstructionCard';
import { QuoteVariantUI } from '../../components/QuoteVariantTabs/QuoteVariant';
import { RequestDetailsCard } from '../../components/RequestDetailsCard';
import { SimpleForm } from '../../components/SimpleForm';
import { Timeline } from '../../components/Timeline';
import { Validity } from '../../components/Validity';
import { TrpcOutputs } from '../../config/trpc';
import { useFlightData } from '../../hooks/useFlightData';

type Props = {
  quote: TrpcOutputs['getFinalQuote']['quote'];
};

export const FcaBookingConfirmation = ({ quote }: Props) => {
  const { flightData } = useFlightData(quote?.flight_path);

  const bookedQuoteVariant = quote?.request?.booked_quote_variant;

  const schema = z.object({
    documents: z
      .array(
        z.object({
          id: z.number(),
          created_at: z.coerce.date().nullish(),
          updated_at: z.coerce.date().nullish(),
          original_filename: z.string(),
          type: z.nativeEnum(DocumentType, { invalid_type_error: 'Invalid Document Type' }).nullish(),
          request_id: z.number().nullish(),
          document_id: z.number().nullish(),
        }),
      )
      .optional(),
  });
  const request = quote?.request;
  const methods = useForm<{
    documents: Array<{
      id: number;
      created_at: Date | null;
      updated_at: Date | null;
      original_filename: string;
      type: DocumentType | null;
      request_id: number | null;
      document_id: number | null;
    }>;
  }>({
    mode: 'onBlur',
    resolver: zodResolver(schema),
    defaultValues: {
      documents: request?.documents || [],
    },
  });

  useEffect(() => {
    methods.reset({
      ...methods.getValues(),
      documents: request?.documents || [],
    });
  }, [request?.documents, methods]);

  const inlandQuote = bookedQuoteVariant?.inland_service_quote_variant?.quote;
  const isFCA = inlandQuote?.freight_service === FreightService.FCA;
  const isEXW = inlandQuote?.freight_service === FreightService.EXW;
  const isInland = isFCA || isEXW;

  const isDgd = useMemo(() => request?.cargo_type === CargoType.DangerousGoods, [request?.cargo_type]);

  const documentTypes = useMemo(() => {
    const suggested: DocumentType[] = [DocumentType.SLI, DocumentType.SHIPPER_DOCUMENTS, DocumentType.AIRWAY_BILL];
    if (isFCA) suggested.push(DocumentType.BOL);
    if (isDgd) suggested.push(DocumentType.DGD, DocumentType.MSDS);

    return suggested;
  }, [isFCA, isDgd]);

  const stopCountLabel = useMemo(() => {
    const numStops = quote?.flight_path?.length - 1;

    if (numStops === 0) return 'Nonstop';
    if (numStops === 1) return '1 stop';
    return `${numStops} stops`;
  }, [quote?.flight_path?.length]);

  return (
    <Stack direction={{ base: 'column', lg: 'row' }} spacing="6">
      <Stack w="full" spacing="6">
        <Show if={!!quote?.booking_instructions && !isInland}>
          <InstructionsCard instructions={quote?.booking_instructions} />
        </Show>
        <Card h="auto" px="4" py="3">
          <CardBody p="0">
            <DropOffSection
              dropOffDate={prettyDate(quote?.dropoff_at, {
                timeZone: quote?.origin_airport?.time_zone,
              })}
              address={inlandQuote?.delivery_address}
              dockHours={`${prettyDockHour(inlandQuote?.from_dock_hours)} - ${prettyDockHour(inlandQuote?.to_dock_hours)}`}
              contact={inlandQuote?.inland_dropoff_contact}
              truckingName={inlandQuote?.provider?.name}
            />
          </CardBody>
        </Card>
        <SimpleForm onSubmit={() => {}} {...(methods as any)}>
          <Card h="fit-content">
            <CardHeader>
              <Heading variant="h5">Documents</Heading>
            </CardHeader>
            <CardBody p="4">
              <DocumentsForm
                name="documents"
                requestId={quote?.request?.id}
                documentTypes={documentTypes}
                isApprovable
              />
            </CardBody>
          </Card>
        </SimpleForm>

        <Card borderRadius="lg" role="group">
          <CardHeader pl="4" pr="4" pt="3" pb="3">
            <Stack>
              <Stack direction="row" align="center" spacing="6">
                <Image src={quote?.provider?.logo_uri} height="48px" width="48px" alt={quote?.provider?.name} />
                <Heading variant="h6">
                  {quote?.origin_airport_id}{' '}
                  <Box as="span" color="gray.400">
                    →
                  </Box>{' '}
                  {quote?.destination_airport_id}
                </Heading>
                <Text textStyle="bodySmallRegular">{stopCountLabel}</Text>
                <CopyButton text={buildFlightPathString(quote?.flight_path)} marginLeft="auto" />
              </Stack>
              <Text color="text.subtle" textStyle="captionSmallRegular">
                {quote?.provider?.name}
              </Text>
            </Stack>
          </CardHeader>
          <Stack>
            <Divider orientation="horizontal" />
          </Stack>
          <CardBody>
            <Timeline data={flightData} />
          </CardBody>
        </Card>
      </Stack>
      <Stack w="full" spacing="6" alignItems="flex-start">
        <Card h="fit-content">
          <CardBody p="4">
            <Stack spacing={6}>
              <Stack direction="row" align="center" justify="space-between" spacing="4" role="group">
                <Heading variant="h5">Cost Summary</Heading>
                <CopyLineItemsButton quote={quote} quoteVariant={bookedQuoteVariant} />
              </Stack>
              <QuoteVariantUI quote={quote} quoteVariant={bookedQuoteVariant} h="fit-content" showCopy={false} />
              <Validity quote={quote} />
            </Stack>
          </CardBody>
        </Card>
        <RequestDetailsCard request={quote?.request} quote={quote} />
      </Stack>
    </Stack>
  );
};
